/**
 *  Singleton acting as a global HTTP Interceptor
 *
 *        - signature's method follows Axios configuration [1]
 *
 *  @link https://gist.github.com/moreta/fb2625c59aa788009b1f7ce8e44ac559 [1]
 */
import APIError from '~/utils/infra/exports/error'
import { causePrism, dataPrism, messagePrism, requestResponsePrism } from '~/utils/infra/exports/request'
import { ERRORS, MICRO_SERVICE_ERRORS, requestResponseReflectionToPromise, responseResponseReflectionPredicate } from '~/utils/infra/exports/types'
import Console from '~/utils/lib/core/console'
import { always, prop, toString, tryCatch } from '~/utils/lib/core/prelude'
import { RetryManager } from '../common/retry-manager'

const console = new Console('plugin:exports:interceptor')

export default {
  /**
   * @function response
   * @this {import('./request').default}
   * @param {import('./types').RequestResponse["_data"]} results
   * @return {Promise<import('./types').RequestResponse>} response
   */
  response(results) {
    if (this.response.status >= 400)
      return Promise.reject(results)

    // Automatic wrap in case where the micro service returns a 2xx reflection
    // response pattern
    const hasRequestReponseReflection
      = responseResponseReflectionPredicate(results)

    switch (hasRequestReponseReflection) {

      case false:
        this.response = results

        return Promise.resolve(this.response)

      case true:

        return requestResponseReflectionToPromise(
          // @ts-ignore: UNSAFE coerce (w/ TypeScript limitation on narrowing)
          /** @type {import('./types').RequestResponseReflection} */ (results)
          )
          .then((/** @type {any} */ res_) => {
            this.response = res_

            return this.response
          })

    }
  },
  /**
   * @function responseError
   * @this {import('~/utils/infra/exports/request').default}
   * @param {import('~/utils/infra/exports/types').RequestError} error
   * @promise-always-rejects
   * @returns {Promise<*>} `APIError`
   */
   responseError(error) {
    // Parsing the promised returned <APIError>
    try {
      const response = requestResponsePrism(error)
      const status = prop('statusCode', error)

      const cause = tryCatch(
        causePrism,
        always(MICRO_SERVICE_ERRORS.MISC)
      )(response)

      const message = tryCatch(
        messagePrism,
        always(`Une erreur serveur est survenue`)
      )(response)

      const payload = tryCatch(
        dataPrism,
        always(null)
      )(response)

      this.error = new APIError(cause, message, payload)

      if (status === 401)
        return this.accept(RetryManager)

      return Promise.reject( this.error )

    } catch (/** @type {any} */ err) {

      logResponseError(err)

      this.error = new APIError(
        MICRO_SERVICE_ERRORS.MISC,
        'Une erreur serveur est survenue'
      )

      return Promise.reject( this.error )
    }
  }
}
/**
* @function logResponseError
* @static
* @param {Error|APIError|any} err
* @return {void}
*/
function logResponseError(err) {
  switch (true) {

    case
      (  err instanceof Error
      && toString(err) === ERRORS.REQUEST_RESPONSE_UNKNOWN_SCHEMA
      ):

      console.warn('caught a <RequestResponse> with unknown schema', err)
      break

    default:

      console.log('caught lambda <RequestError>', err)
      break
  }
}
